import { useEffect, useMemo, useState, useCallback } from "react";
import api, { errorMessage, startImpersonation } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import ViewToggle, { usePersistedView } from "../components/ViewToggle";
import {
  Plus, Pencil, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown, Building2,
  DollarSign, Activity, AlertOctagon, MapPinned, MapPin, Phone, Mail, FileText, Power, Download, Users, SlidersHorizontal, UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { formatAddress, distinctLocations } from "../lib/address";
import { roleLabel } from "../lib/roles";

const emptyClient = {
  company_name: "",
  address: "",
  street_address: "",
  city: "",
  state: "",
  zip_code: "",
  email: "",
  phone: "",
  contact_person: "",
  contact_email: "",
  contact_phone: "",
  status: "active",
  notes: "",
  portfolios: [],
};

const STATUS_OPTIONS = [
  { value: "active", label: "Active", tone: "green" },
  { value: "inactive", label: "Inactive", tone: "red" },
  { value: "ended", label: "Ended", tone: "amber" },
];

function SortableHeader({ k, sortKey, sortDir, onSort, children }) {
  const active = sortKey === k;
  return (
    <th className="px-4 py-3 text-left">
      <button type="button" data-testid={`sort-${k}`} onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 label-mute hover:text-white transition-colors ${active ? "text-[#FF3B30]" : ""}`}>
        {children}
        {active ? (sortDir === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />) : <ArrowUpDown className="size-3 opacity-40" />}
      </button>
    </th>
  );
}

function fmtMoney(v) {
  if (!v) return "$0";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(v)}`;
}

function relTime(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return new Date(iso).toLocaleDateString();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function StatCell({ icon: Icon, value, label, tone = "default", testId }) {
  const toneClass = ({ green: "text-[#34C759]", amber: "text-[#FF9F0A]", red: "text-[#FF3B30]", blue: "text-[#0A84FF]" }[tone]) || "text-zinc-300";
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <Icon className={`size-3.5 ${toneClass} shrink-0`} />
      <div className="leading-tight">
        <div className={`font-mono font-bold text-sm ${toneClass}`}>{value}</div>
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      </div>
    </div>
  );
}

function ClientDetailModal({ clientId, onClose, onEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    api.get(`/clients/${clientId}/detail`).then((r) => { if (active) { setData(r.data); setLoading(false); } })
      .catch(() => { if (active) { toast.error("Failed to load client detail"); setLoading(false); } });
    return () => { active = false; };
  }, [clientId]);

  if (loading || !data) {
    return (
      <Modal title="Client" onClose={onClose} testId="client-detail-modal">
        <div className="py-12 text-center text-zinc-500 text-sm scanlines">Loading…</div>
      </Modal>
    );
  }
  const { client, sites, recent_incidents: incidents, portal_users: portalUsers = [], kpis } = data;
  const statusTone = client.status === "inactive" ? "red" : "green";
  return (
    <Modal title={client.company_name} onClose={onClose} testId="client-detail-modal">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <PillBadge tone={statusTone} data-testid="detail-status-badge">{client.status || "active"}</PillBadge>
          <div className="ml-auto">
            <GhostButton data-testid="detail-edit-button" onClick={() => onEdit(client)}>
              <Pencil className="size-3.5" /> Edit
            </GhostButton>
          </div>
        </div>

        {/* KPI mini grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="detail-kpis">
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute mb-1">Active Sites</div>
            <div className="font-display text-xl font-bold text-[#34C759]">{kpis.active_sites}<span className="text-zinc-500 text-sm">/{kpis.total_sites}</span></div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute mb-1">Contract</div>
            <div className="font-display text-xl font-bold text-[#0A84FF]">{fmtMoney(kpis.total_contract_value)}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute mb-1">Open / Wk</div>
            <div className="font-display text-xl font-bold text-[#FF9F0A]">{kpis.open_shifts_this_week}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute mb-1">Last Incident</div>
            <div className="font-display text-base font-bold text-[#FF3B30]">{relTime(kpis.last_incident_at) || "—"}</div>
          </div>
        </div>

        {/* Contact / address */}
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div className="border border-[#27272A] p-4 space-y-1.5">
            <div className="label-mute flex items-center gap-1.5 mb-2"><MapPin className="size-3" /> Address</div>
            <div className="text-zinc-300">{formatAddress(client) || <span className="text-zinc-500">No address</span>}</div>
          </div>
          <div className="border border-[#27272A] p-4 space-y-1.5">
            <div className="label-mute flex items-center gap-1.5 mb-2"><Phone className="size-3" /> Contact</div>
            {client.contact_person && <div className="text-zinc-300">{client.contact_person}</div>}
            {(client.contact_phone || client.phone) && <div className="font-mono text-zinc-400 text-xs">{client.contact_phone || client.phone}</div>}
            {(client.contact_email || client.email) && <div className="font-mono text-zinc-400 text-xs flex items-center gap-1"><Mail className="size-3" />{client.contact_email || client.email}</div>}
            {!client.contact_person && !client.phone && !client.email && <div className="text-zinc-500">No contact</div>}
          </div>
        </div>

        {client.notes && (
          <div className="border border-[#27272A] p-4">
            <div className="label-mute flex items-center gap-1.5 mb-2"><FileText className="size-3" /> Notes</div>
            <div className="text-sm whitespace-pre-wrap text-zinc-300">{client.notes}</div>
          </div>
        )}

        {/* Sites list */}
        <div>
          <div className="label-mute mb-2 flex items-center gap-1.5"><Building2 className="size-3" /> Sites ({sites.length})</div>
          <div className="border border-[#27272A] divide-y divide-[#27272A]">
            {sites.length === 0 && <div className="p-4 text-zinc-500 text-sm">No sites linked.</div>}
            {sites.map((s) => (
              <div key={s.id} className="p-3 flex items-center justify-between gap-2 hover:bg-[#1c1c1e]" data-testid={`detail-site-${s.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{formatAddress(s)}</div>
                </div>
                <PillBadge tone={s.status === "active" ? "green" : s.status === "paused" ? "amber" : "default"}>{s.status || "active"}</PillBadge>
              </div>
            ))}
          </div>
        </div>

        {/* Portal users */}
        <div>
          <div className="label-mute mb-2 flex items-center gap-1.5"><Users className="size-3" /> Portal Users ({portalUsers.length})</div>
          <div className="border border-[#27272A] divide-y divide-[#27272A]" data-testid="detail-portal-users">
            {portalUsers.length === 0 && <div className="p-4 text-zinc-500 text-sm">No portal users linked.</div>}
            {portalUsers.map((u) => {
              const scopeCount = Array.isArray(u.assigned_site_ids) ? u.assigned_site_ids.length : 0;
              const roleTone = u.role === "client_admin" ? "blue" : u.role === "regional_manager" ? "amber" : "default";
              return (
                <div key={u.id} className="p-3 flex items-center justify-between gap-3" data-testid={`detail-portal-user-${u.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.name || u.email}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 truncate flex items-center gap-1">
                      <Mail className="size-3" /> {u.email}
                    </div>
                    {(u.role === "regional_manager" || u.role === "site_manager") && (
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        {scopeCount === 0 ? "no sites assigned" : `${scopeCount} site${scopeCount === 1 ? "" : "s"} in scope`}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <PillBadge tone={roleTone}>{roleLabel(u.role)}</PillBadge>
                    <GhostButton
                      data-testid={`impersonate-btn-${u.id}`}
                      onClick={async () => {
                        try {
                          const res = await api.post(`/admin/impersonate/${u.id}`);
                          startImpersonation(res.data.access_token || res.data.token, res.data.target);
                          toast.success(`Now viewing as ${res.data.target?.name || res.data.target?.email}`);
                          // Open the portal in a new tab so the admin retains
                          // their real cookie session in the original tab.
                          window.open("/portal", "_blank", "noopener");
                        } catch (err) {
                          toast.error(errorMessage(err));
                        }
                      }}
                      title="Open the client portal in a new tab as this user (30-min shadow session)"
                    >
                      <UserCog className="size-3.5" /> View as
                    </GhostButton>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent incidents */}
        <div>
          <div className="label-mute mb-2 flex items-center gap-1.5"><AlertOctagon className="size-3" /> Recent Incidents</div>
          <div className="border border-[#27272A] divide-y divide-[#27272A]">
            {incidents.length === 0 && <div className="p-4 text-zinc-500 text-sm">No incidents reported.</div>}
            {incidents.map((i) => (
              <div key={i.id} className="p-3" data-testid={`detail-incident-${i.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium truncate">{i.title}</div>
                  <PillBadge tone={({ critical: "red", high: "red", medium: "amber", low: "default" }[i.severity]) || "default"}>{i.severity}</PillBadge>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                  {i.site_name} · {new Date(i.created_at).toLocaleString()} · status: {i.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default function Clients() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [kpis, setKpis] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyClient);
  // Field-level errors surfaced inline (e.g. Pydantic 422 email validation).
  // Keyed by form field name → human-readable message. Cleared on modal open
  // and on every subsequent Save attempt so stale errors never linger.
  const [fieldErrors, setFieldErrors] = useState({});
  const [detailId, setDetailId] = useState(null);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [sortKey, setSortKey] = useState("company_name");
  const [sortDir, setSortDir] = useState("asc");
  const [deactivateTarget, setDeactivateTarget] = useState(null); // { client }
  const [deactivateStatus, setDeactivateStatus] = useState("on-hold");
  const [deactivateCascade, setDeactivateCascade] = useState(false);

  const load = useCallback(async () => {
    const [c, s, k] = await Promise.all([api.get("/clients"), api.get("/sites"), api.get("/clients/kpis")]);
    setItems(c.data); setSites(s.data);
    const map = {};
    (k.data || []).forEach((row) => { map[row.client_id] = row; });
    setKpis(map);
  }, []);
  useEffect(() => { load(); }, [load]);

  const siteCountByClient = useMemo(() => {
    const m = {};
    sites.forEach((s) => { if (s.client_id) m[s.client_id] = (m[s.client_id] || 0) + 1; });
    return m;
  }, [sites]);

  const onSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };

  const locOptions = useMemo(() => distinctLocations(items), [items]);

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const citySet = new Set(cityFilter);
    const stateSet = new Set(stateFilter);
    const statusSet = new Set(statusFilter);
    const filtered = items.filter((c) => {
      if (q && !(
        (c.company_name || "").toLowerCase().includes(q) ||
        (c.address || "").toLowerCase().includes(q) ||
        (c.street_address || "").toLowerCase().includes(q) ||
        (c.contact_person || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.contact_email || "").toLowerCase().includes(q)
      )) return false;
      if (citySet.size && !citySet.has((c.city || "").trim())) return false;
      if (stateSet.size && !stateSet.has((c.state || "").trim())) return false;
      if (statusSet.size && !statusSet.has(c.status || "active")) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = (a[sortKey] || "").toString().toLowerCase();
      const bv = (b[sortKey] || "").toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [items, search, cityFilter, stateFilter, statusFilter, sortKey, sortDir]);

  const openNew = () => { setEditing(null); setForm(emptyClient); setFieldErrors({}); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyClient, ...c }); setFieldErrors({}); setDetailId(null); setOpen(true); };

  // Convert an axios error's FastAPI 422 payload into a { field: message } map
  // so we can surface the offender inline. Falls back to `null` when the error
  // isn't a validation array (e.g. plain 400/500) — caller then uses the toast.
  const parseFieldErrors = (err) => {
    const detail = err?.response?.data?.detail;
    if (!Array.isArray(detail)) return null;
    const map = {};
    for (const it of detail) {
      // Pydantic gives us loc = ["body", "email"] — anything after "body" is
      // the field path; join with "." so nested paths remain distinguishable.
      const loc = Array.isArray(it?.loc) ? it.loc.filter((p) => p !== "body") : [];
      if (loc.length === 0) continue;
      const key = loc.join(".");
      if (!map[key]) map[key] = it?.msg || "Invalid value";
    }
    return Object.keys(map).length > 0 ? map : null;
  };

  const onSave = async () => {
    setFieldErrors({});
    if (!form.company_name?.trim()) { toast.error("Company Name is required"); return; }
    if (!form.address?.trim() && !form.street_address?.trim()) {
      toast.error("Provide an address (street + city + state + zip, or the legacy single line)"); return;
    }
    // Synthesize the legacy `address` field from the split parts when it's empty,
    // so the backend's required `address` field is satisfied without forcing users
    // to type it twice.
    const synthesized = !form.address?.trim()
      ? [form.street_address, form.city, form.state, form.zip_code].filter(Boolean).join(", ")
      : form.address.trim();
    const payload = Object.fromEntries(
      Object.entries({ ...form, address: synthesized }).filter(([, v]) => v !== "" && v != null)
    );
    payload.company_name = form.company_name.trim();
    payload.address = synthesized;
    payload.status = form.status || "active";
    try {
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false); setEditing(null); setForm(emptyClient); setFieldErrors({});
      load();
    } catch (e) {
      const fe = parseFieldErrors(e);
      if (fe) {
        setFieldErrors(fe);
        // Prefer the first field message in the toast too so users on small
        // screens don't have to hunt for the inline hint.
        const first = Object.values(fe)[0];
        toast.error(first || "Please fix the highlighted field");
        return;
      }
      toast.error(errorMessage(e, "Save failed"));
    }
  };

  const onDelete = (c) => {
    setDeactivateStatus("on-hold");
    setDeactivateCascade(false);
    setDeactivateTarget({ client: c });
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    const { client } = deactivateTarget;
    const status = deactivateStatus;
    const cascade = deactivateCascade && (status === "ended" || status === "inactive");
    setDeactivateTarget(null);
    try {
      const r = await api.delete(`/clients/${client.id}?status=${status}${cascade ? "&cascade=true" : ""}`);
      const cs = r.data?.cascaded_sites?.length || 0;
      const cn = r.data?.cancelled_shifts || 0;
      toast.success(`"${client.company_name}" → ${status}${cs ? ` · ${cs} site${cs === 1 ? "" : "s"} ended, ${cn} shift${cn === 1 ? "" : "s"} cancelled` : ""}`);
      load();
    } catch (e) { toast.error(errorMessage(e, "Deactivation failed")); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/clients/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "clients.csv"; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errorMessage(e, "Export failed")); }
  };

  const [view, setView] = usePersistedView("clients_view");

  return (
    <div>
      <PageHeader title="Clients" subtitle="Companies under contract. Sites are linked to a client from the Sites tab."
        action={
          <div className="inline-flex items-center gap-3">
            <ViewToggle testIdPrefix="clients" value={view} onChange={setView} />
            <GhostButton data-testid="clients-export-csv" onClick={exportCsv} title="Download clients as CSV">
              <Download className="size-4" /> CSV
            </GhostButton>
            <ActionButton data-testid="add-client-button" onClick={openNew}><Plus className="size-4" /> New Client</ActionButton>
          </div>
        } />

      <div className="p-8 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="clients-portfolio">
          <div className="border border-[#27272A] bg-[#121212] p-4">
            <div className="label-mute mb-1 flex items-center gap-1.5"><Building2 className="size-3" /> Active Clients</div>
            <div className="font-display text-2xl font-bold">{items.filter((c) => (c.status || "active") === "active").length}</div>
          </div>
          <div className="border border-[#27272A] bg-[#121212] p-4">
            <div className="label-mute mb-1 flex items-center gap-1.5"><MapPinned className="size-3 text-[#34C759]" /> Active Sites</div>
            <div className="font-display text-2xl font-bold text-[#34C759]">{Object.values(kpis).reduce((sum, k) => sum + (k.active_sites || 0), 0)}</div>
          </div>
          <div className="border border-[#27272A] bg-[#121212] p-4">
            <div className="label-mute mb-1 flex items-center gap-1.5"><DollarSign className="size-3 text-[#0A84FF]" /> Contract Book</div>
            <div className="font-display text-2xl font-bold text-[#0A84FF]">{fmtMoney(Object.values(kpis).reduce((sum, k) => sum + (k.total_contract_value || 0), 0))}</div>
          </div>
          <div className="border border-[#27272A] bg-[#121212] p-4">
            <div className="label-mute mb-1 flex items-center gap-1.5"><Activity className="size-3 text-[#FF9F0A]" /> Open Shifts (This Wk)</div>
            <div className="font-display text-2xl font-bold text-[#FF9F0A]">{Object.values(kpis).reduce((sum, k) => sum + (k.open_shifts_this_week || 0), 0)}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3" data-testid="clients-filters">
          <div className="flex-1 min-w-[240px] max-w-sm">
            <div className="label-mute mb-1">Search</div>
            <div className="relative">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input data-testid="filter-search" className={`${inputClass} pl-10`}
                placeholder="Search by company, address, contact, email…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><Power className="size-3" /> Status</div>
            <MultiSelectDropdown testId="filter-status" label="All statuses"
              options={STATUS_OPTIONS} selected={statusFilter} onChange={setStatusFilter} triggerClassName="min-w-[150px]" />
          </div>
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><MapPin className="size-3" /> City</div>
            <MultiSelectDropdown testId="filter-city" label="All cities"
              options={locOptions.cities} selected={cityFilter} onChange={setCityFilter} searchable triggerClassName="min-w-[170px]" emptyHint="No cities yet" />
          </div>
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><MapPin className="size-3" /> State</div>
            <MultiSelectDropdown testId="filter-state" label="All states"
              options={locOptions.states} selected={stateFilter} onChange={setStateFilter} searchable triggerClassName="min-w-[140px]" emptyHint="No states yet" />
          </div>
          {(cityFilter.length || stateFilter.length || statusFilter.length || search) > 0 && (
            <button data-testid="clear-filters"
              onClick={() => { setSearch(""); setCityFilter([]); setStateFilter([]); setStatusFilter([]); }}
              className="text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] pb-2">Clear</button>
          )}
          <div className="ml-auto text-xs font-mono text-zinc-500 pb-2">{visibleItems.length} / {items.length} clients</div>
        </div>

        {view === "list" ? (
        <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead className="border-b border-[#27272A]">
              <tr>
                <th className="px-2 py-3 w-[40px] text-center"><div className="label-mute">#</div></th>
                <SortableHeader k="company_name" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Company</SortableHeader>
                <SortableHeader k="contact_person" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Contact</SortableHeader>
                <th className="px-4 py-3 text-left"><div className="label-mute">Status</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Sites</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Contract $</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Open / Wk</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Last Incident</div></th>
                <th className="px-4 py-3 text-right"><div className="label-mute">Actions</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {visibleItems.map((c, idx) => {
                const k = kpis[c.id] || {};
                const activeSites = k.active_sites || 0;
                const totalSites = k.total_sites || siteCountByClient[c.id] || 0;
                const openShifts = k.open_shifts_this_week || 0;
                const lastInc = relTime(k.last_incident_at);
                const status = c.status || "active";
                return (
                  <tr key={c.id} data-testid={`client-row-${c.id}`}
                    onClick={() => setDetailId(c.id)}
                    className="hover:bg-[#1c1c1e] cursor-pointer">
                    <td className="px-2 py-3 text-center font-mono text-zinc-500" data-testid={`row-num-${c.id}`}>{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-display font-bold uppercase tracking-tight flex items-center gap-2">
                        <Building2 className="size-3.5 text-[#FF3B30]" /> {c.company_name}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 truncate max-w-[260px]">{formatAddress(c)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-zinc-300">{c.contact_person || "—"}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono truncate max-w-[180px]">
                        {c.contact_phone || c.phone || ""}{(c.contact_phone || c.phone) && (c.contact_email || c.email) ? " · " : ""}{c.contact_email || c.email || ""}
                      </div>
                    </td>
                    <td className="px-4 py-3"><PillBadge tone={status === "inactive" ? "red" : "green"} data-testid={`status-${c.id}`}>{status}</PillBadge></td>
                    <td className="px-4 py-3">
                      <StatCell testId={`kpi-sites-${c.id}`} icon={MapPinned}
                        value={activeSites === totalSites ? activeSites : `${activeSites}/${totalSites}`}
                        label={totalSites === 1 ? "site" : "sites"} tone={activeSites > 0 ? "green" : "default"} />
                    </td>
                    <td className="px-4 py-3"><StatCell testId={`kpi-contract-${c.id}`} icon={DollarSign} value={fmtMoney(k.total_contract_value)} label="contract" tone={(k.total_contract_value || 0) > 0 ? "blue" : "default"} /></td>
                    <td className="px-4 py-3"><StatCell testId={`kpi-open-${c.id}`} icon={Activity} value={openShifts} label="open + understaffed" tone={openShifts > 0 ? "amber" : "default"} /></td>
                    <td className="px-4 py-3"><StatCell testId={`kpi-incident-${c.id}`} icon={AlertOctagon} value={lastInc || "—"} label={lastInc ? "last incident" : "no incidents"} tone={lastInc ? "red" : "default"} /></td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex gap-1">
                        <GhostButton data-testid={`portal-config-${c.id}`} title="Client portal config" onClick={() => nav(`/clients/${c.id}/portal-config`)}><SlidersHorizontal className="size-3.5" /></GhostButton>
                        <GhostButton data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)}><Pencil className="size-3.5" /></GhostButton>
                        <GhostButton data-testid={`del-client-${c.id}`} onClick={() => onDelete(c)}><Trash2 className="size-3.5" /></GhostButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleItems.length === 0 && (
                <tr><td colSpan={9} className="text-center py-16 text-zinc-500 scanlines">
                  {items.length === 0 ? "No clients yet. Add your first client to start scheduling sites." : "No clients match the filters."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="clients-grid">
          {visibleItems.map((c) => {
            const k = kpis[c.id] || {};
            const status = c.status || "active";
            return (
              <div
                key={c.id}
                data-testid={`client-card-${c.id}`}
                onClick={() => setDetailId(c.id)}
                className="border border-[#27272A] bg-[#121212] p-4 hover:border-[#FF3B30] transition-colors cursor-pointer flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold uppercase tracking-tight flex items-center gap-2 text-base">
                      <Building2 className="size-4 text-[#FF3B30]" /> <span className="truncate">{c.company_name}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 truncate">{formatAddress(c)}</div>
                  </div>
                  <PillBadge tone={status === "inactive" ? "red" : "green"}>{status}</PillBadge>
                </div>

                <div className="text-xs text-zinc-400">
                  <div className="text-zinc-300 font-medium">{c.contact_person || "—"}</div>
                  <div className="text-zinc-500 font-mono text-[11px] truncate">
                    {c.contact_phone || c.phone || ""}
                    {(c.contact_phone || c.phone) && (c.contact_email || c.email) ? " · " : ""}
                    {c.contact_email || c.email || ""}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-auto border-t border-[#27272A] pt-3">
                  <div>
                    <div className="label-mute">Sites</div>
                    <div className="font-mono text-sm text-[#34C759]">{k.active_sites || 0}</div>
                  </div>
                  <div>
                    <div className="label-mute">Contract</div>
                    <div className="font-mono text-sm text-[#0A84FF]">{fmtMoney(k.total_contract_value)}</div>
                  </div>
                  <div>
                    <div className="label-mute">Open/Wk</div>
                    <div className={`font-mono text-sm ${(k.open_shifts_this_week || 0) > 0 ? "text-[#FF9F0A]" : "text-zinc-400"}`}>{k.open_shifts_this_week || 0}</div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <GhostButton data-testid={`portal-config-${c.id}`} title="Client portal config" onClick={() => nav(`/clients/${c.id}/portal-config`)}><SlidersHorizontal className="size-3.5" /></GhostButton>
                  <GhostButton data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)}><Pencil className="size-3.5" /></GhostButton>
                  <GhostButton data-testid={`del-client-${c.id}`} onClick={() => onDelete(c)}><Trash2 className="size-3.5" /></GhostButton>
                </div>
              </div>
            );
          })}
          {visibleItems.length === 0 && (
            <div className="border border-[#27272A] bg-[#121212] p-12 text-center text-zinc-500 scanlines md:col-span-2 xl:col-span-3">
              {items.length === 0 ? "No clients yet. Add your first client to start scheduling sites." : "No clients match the filters."}
            </div>
          )}
        </div>
        )}
      </div>

      {open && (
        <Modal title={editing ? "Edit Client" : "Add Client"} onClose={() => setOpen(false)} testId="client-form-modal">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Company Name *">
              <input data-testid="client-company-name" required className={inputClass} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </FormField>
            <FormField label="Status">
              <select data-testid="client-status" className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FormField>

            {/* Address — structured */}
            <div className="col-span-2 border-t border-[#27272A] pt-3">
              <div className="label-mute mb-2 flex items-center gap-1.5"><MapPin className="size-3" /> Address</div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Street (Number + Name)">
                  <input data-testid="client-street" className={inputClass} placeholder="123 Main St"
                    value={form.street_address} onChange={(e) => setForm({ ...form, street_address: e.target.value })} />
                </FormField>
                <FormField label="City">
                  <input data-testid="client-city" className={inputClass} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </FormField>
                <FormField label="State">
                  <input data-testid="client-state" className={inputClass} placeholder="NY" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                </FormField>
                <FormField label="Zip / Postal Code">
                  <input data-testid="client-zip" className={inputClass} value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
                </FormField>
              </div>
              {/* Legacy single-line — visible only when no split parts and we're editing an old record */}
              {(editing && !form.street_address && !form.city && form.address) && (
                <div className="mt-3">
                  <FormField label="Legacy address (one-line — re-enter the parts above to migrate)">
                    <input data-testid="client-address" className={inputClass} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                  </FormField>
                </div>
              )}
            </div>

            {/* Company contact */}
            <FormField label="Company Phone">
              <input data-testid="client-phone" className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </FormField>
            <FormField label="Company Email">
              <input
                data-testid="client-email"
                type="email"
                className={`${inputClass} ${fieldErrors.email ? "!border-[#FF3B30]" : ""}`}
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  if (fieldErrors.email) setFieldErrors(({ email: _e, ...rest }) => rest);
                }}
                aria-invalid={fieldErrors.email ? "true" : "false"}
                aria-describedby={fieldErrors.email ? "client-email-error" : undefined}
              />
              {fieldErrors.email && (
                <div
                  id="client-email-error"
                  data-testid="client-email-error"
                  className="mt-1 text-[11px] text-[#FF3B30] leading-snug"
                >
                  {fieldErrors.email}
                </div>
              )}
            </FormField>

            <FormField label="Contact Person">
              <input data-testid="client-contact-person" className={inputClass} value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
            </FormField>
            <FormField label="Contact Phone">
              <input data-testid="client-contact-phone" className={inputClass} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            </FormField>
            <div className="col-span-2">
              <FormField label="Contact Email">
                <input
                  data-testid="client-contact-email"
                  type="email"
                  className={`${inputClass} ${fieldErrors.contact_email ? "!border-[#FF3B30]" : ""}`}
                  value={form.contact_email}
                  onChange={(e) => {
                    setForm({ ...form, contact_email: e.target.value });
                    if (fieldErrors.contact_email) setFieldErrors(({ contact_email: _e, ...rest }) => rest);
                  }}
                  aria-invalid={fieldErrors.contact_email ? "true" : "false"}
                  aria-describedby={fieldErrors.contact_email ? "client-contact-email-error" : undefined}
                />
                {fieldErrors.contact_email && (
                  <div
                    id="client-contact-email-error"
                    data-testid="client-contact-email-error"
                    className="mt-1 text-[11px] text-[#FF3B30] leading-snug"
                  >
                    {fieldErrors.contact_email}
                  </div>
                )}
              </FormField>
            </div>
            <div className="col-span-2">
              <FormField label="Notes">
                <textarea data-testid="client-notes" rows={3} className={inputClass} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes about this client" />
              </FormField>
            </div>
            <div className="col-span-2 border-t border-[#27272A] pt-4">
              <div className="label-mute mb-2">Portfolios</div>
              <div className="text-xs text-zinc-500 mb-3">
                Group this client&apos;s sites into named portfolios (e.g. &quot;West Region&quot;, &quot;Retail Chain A&quot;). Sites can then be filed under a portfolio from their own edit form.
              </div>
              <div className="flex items-center gap-2 flex-wrap" data-testid="client-portfolios">
                {(form.portfolios || []).map((p, idx) => (
                  <span key={p.id || idx} className="inline-flex items-center gap-1 px-2 py-1 bg-[#121212] border border-[#27272A] text-xs" data-testid={`portfolio-chip-${p.id || idx}`}>
                    {p.name}
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, portfolios: form.portfolios.filter((_, i) => i !== idx) })}
                      className="text-zinc-500 hover:text-[#FF3B30] ml-1"
                      title="Remove portfolio"
                      data-testid={`portfolio-remove-${p.id || idx}`}
                    >×</button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder="+ Add portfolio and press Enter"
                  data-testid="portfolio-add-input"
                  className={`${inputClass} !w-56 !py-1`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.currentTarget.value.trim()) {
                      e.preventDefault();
                      const name = e.currentTarget.value.trim();
                      const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
                      setForm({ ...form, portfolios: [...(form.portfolios || []), { id, name }] });
                      e.currentTarget.value = "";
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
            <ActionButton data-testid="save-client-button" onClick={onSave}>{editing ? "Update Client" : "Save Client"}</ActionButton>
          </div>
        </Modal>
      )}

      {detailId && (
        <ClientDetailModal clientId={detailId} onClose={() => setDetailId(null)} onEdit={(c) => { setDetailId(null); openEdit(c); }} />
      )}

      {deactivateTarget && (() => {
        const client = deactivateTarget.client;
        const linkedSites = siteCountByClient[client.id] || 0;
        const cascadeAvailable = deactivateStatus === "ended" || deactivateStatus === "inactive";
        return (
          <Modal title={`Deactivate client — ${client.company_name}`} onClose={() => setDeactivateTarget(null)} testId="client-deactivate-modal">
            <div className="space-y-4">
              {linkedSites > 0 && (
                <div className="text-xs text-[#FF9F0A] border border-[#FF9F0A]/30 bg-[#FF9F0A]/5 p-3">
                  This client has <b>{linkedSites}</b> site{linkedSites === 1 ? "" : "s"} attached.
                </div>
              )}
              <div className="text-xs text-zinc-500">Choose the new status:</div>
              <div className="space-y-2">
                {[
                  { v: "active", label: "Active", hint: "Currently under contract" },
                  { v: "on-hold", label: "On-hold", hint: "Contract paused · sites untouched · no cascade" },
                  { v: "inactive", label: "Inactive", hint: "Marked inactive · sites untouched by default" },
                  { v: "ended", label: "Ended", hint: "Contract terminated · sites untouched by default" },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    data-testid={`client-deactivate-option-${opt.v}`}
                    className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${deactivateStatus === opt.v ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-[#27272A] hover:border-zinc-600"}`}
                  >
                    <input
                      type="radio"
                      name="client-status"
                      checked={deactivateStatus === opt.v}
                      onChange={() => setDeactivateStatus(opt.v)}
                      className="mt-1"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold uppercase tracking-wider">{opt.label}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{opt.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
              {cascadeAvailable && linkedSites > 0 && (
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer" data-testid="client-deactivate-cascade">
                  <input
                    type="checkbox"
                    checked={deactivateCascade}
                    onChange={(e) => setDeactivateCascade(e.target.checked)}
                  />
                  <span>Also end all <b>{linkedSites}</b> attached site{linkedSites === 1 ? "" : "s"} and CANCEL their future shifts</span>
                </label>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <GhostButton data-testid="client-deactivate-cancel" onClick={() => setDeactivateTarget(null)}>Cancel</GhostButton>
                <ActionButton data-testid="client-deactivate-confirm" onClick={confirmDeactivate}>Update status</ActionButton>
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}
